import qs from 'qs';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { EntityType, QueryKeys } from 'shared/constants/entityTypes';
import { DEFAULT_PAGE_SIZE } from 'shared/constants/misc';
import { axiosinstance } from 'shared/utils/api';
import { noop } from 'shared/utils/misc';
import { useRequestParams } from '../api/misc';

const appendStandardTurnaround = (res = {}) => ({
  ...res,
  data: [{ id: null, name: 'Standard' }, ...res.data],
});

function _loadEntityOptions(entityName, labelFn, valueFn) {
  let queryState = {};
  const queryClient = useQueryClient();

  async function loadOptions(params = {}) {
    const queryKey = [entityName, 'options', params];
    const queryFn = async () => {
      const { data: res } = await axiosinstance.get(
        `/v1/${entityName.toLowerCase()}`,
        {
          params,
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: 'repeat' }),
        }
      );

      // Turnaround needs a special handling where 
      // we append a "Standard" option as default.
      return entityName === QueryKeys[EntityType.TURNAROUND]
        ? appendStandardTurnaround(res)
        : res;
    };
    const {
      data: options = [],
      total_pages: total,
      page_number: current,
    } = await queryClient.fetchQuery(queryKey, queryFn);
    queryState = queryClient.getQueryState(queryKey);

    return {
      options: options.map((obj) => ({
        label: labelFn(obj),
        value: valueFn(obj),
      })),
      hasMore: current < total,
      additional: { page: params.page + 1 },
    };
  }

  return [loadOptions, queryState];
}

export function useAsyncSelect({
  queryKey = '',
  reqParams = {},
  getOptionLabel = noop,
  getOptionValue = noop,
} = {}) {
  const params = useRequestParams(reqParams);
  const [fetchEntity, queryState] = _loadEntityOptions(
    queryKey,
    getOptionLabel,
    getOptionValue
  );
  const loadOptions = useCallback(
    async (search, _loadedOptions, { page }) =>
      await fetchEntity({ ...params, search, page }),
    [fetchEntity, params]
  );

  return {
    ...queryState,
    loadOptions,
    debounceTimeout: 600,
    additional: { page: 1 },
    maxMenuHeight: DEFAULT_PAGE_SIZE * 10,
  };
}
